/**
 * License Intelligence Dashboard
 * Main page component for license management and analytics
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';

import {
  AddLicensePanel,
  fetchHeaderData,
  fetchLicenseDetails,
  GraphicalView,
  Header,
  KPICards,
  LicenseDetailsPanel,
  type LicenseFormData,
  LicenseInventoryContent,
  Tabs,
} from '../../features/LicenseManagement';
import kpiCardsData from '../../features/LicenseManagement/components/Dashboard/KPICards/mockData';
import type { LicenseDetail } from '../../features/LicenseManagement/components/Dashboard/LicenseDetailsPanel/type';
import { globalActions } from '../../shared/store/globalSlice';
import type { RootState } from '../../shared/types';

import { VIEW_MODES, type ViewMode } from './type';

import './LicenseIntelligence.css';

/**
 * Main dashboard component for License Intelligence. Displays compliance metrics, license data, and management tools.
 */
function LicenseIntelligence(): React.ReactElement {
  const dispatch = useDispatch();
  const { dataTheme } = useSelector((state: RootState) => state.aag.global);
  const [complianceScore, setComplianceScore] = useState(60);
  const [lastSyncDate, setLastSyncDate] = useState('Jan 12, 2026 1:24 PM');
  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_MODES.LIST);
  const [isAddLicensePanelOpen, setIsAddLicensePanelOpen] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<
    LicenseDetail | undefined
  >();

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('li-theme') as
      | 'light'
      | 'dark'
      | null;

    if (savedTheme) {
      dispatch(globalActions.setDataTheme(savedTheme));
    } else {
      // Default to dark theme
      dispatch(globalActions.setDataTheme('dark'));
    }
  }, [dispatch]);

  // Apply data-theme to document root so design system tokens can detect it
  useEffect(() => {
    if (dataTheme) {
      document.documentElement.setAttribute('data-theme', dataTheme);
    }
  }, [dataTheme]);

  // Fetch header data on mount
  useEffect(() => {
    const loadHeaderData = async (): Promise<void> => {
      try {
        const headerData = await fetchHeaderData();

        setComplianceScore(headerData.complianceScore);
        setLastSyncDate(headerData.lastSyncDate);
      } catch (error) {
        // Keep default values on error
      }
    };

    loadHeaderData().catch(() => {
      // Error already handled in loadHeaderData
    });
  }, []);

  // KPI Cards Data
  const handleAddLicense = (): void => {
    setIsAddLicensePanelOpen(true);
  };

  const handleAddLicensePanelClose = (): void => {
    setIsAddLicensePanelOpen(false);
  };

  const handleAddLicenseSave = (licenseData: LicenseFormData): void => {
    // Send license data to API
    Logger.log('License saved:', licenseData);
  };

  const handleAddWidget = (): void => {
    Logger.log('Add Widget clicked');
  };

  const handleViewModeChange = (mode: ViewMode): void => {
    setViewMode(mode);
    Logger.log('View mode changed to:', mode);
  };

  const handleSortChange = (sortOption: string): void => {
    Logger.log('Sort changed to:', sortOption);
  };

  const handleSelectLicense = (licenseId: string): void => {
    Logger.log('License selected:', licenseId);

    // Fetch license details asynchronously without blocking the handler
    fetchLicenseDetails(licenseId)
      .then((licenseDetail) => {
        if (licenseDetail && typeof licenseDetail === 'object') {
          setSelectedLicense(licenseDetail as LicenseDetail);
          setIsDetailsPanelOpen(true);
        }
      })
      .catch(() => {
        // Handle error silently
      });
  };

  // Render content based on view mode
  const renderTabContent = () => {
    if (viewMode === VIEW_MODES.GRAPHICAL) {
      return <GraphicalView />;
    }

    return <LicenseInventoryContent onSelectLicense={handleSelectLicense} />;
  };

  return (
    <>
      <div className='license-intelligense' data-theme={dataTheme}>
        {/* Fixed Header and KPI Cards */}
        <div className='license-intelligense-header'>
          <Header
            complianceScore={complianceScore}
            lastSyncDate={lastSyncDate}
            onAddLicense={handleAddLicense}
            onAddWidget={handleAddWidget}
          />

          <KPICards cards={kpiCardsData} />

          <Tabs
            onViewModeChange={handleViewModeChange}
            viewMode={viewMode}
            sortBy='By Annual Cost'
            onSortChange={handleSortChange}
            onSettingsClick={() => Logger.log('Settings clicked')}
          />
        </div>

        {/* Scrollable Content Area */}
        <div className='license-intelligense-scrollArea'>
          {renderTabContent()}
        </div>
      </div>

      <AddLicensePanel
        isOpen={isAddLicensePanelOpen}
        onClose={handleAddLicensePanelClose}
        onSave={handleAddLicenseSave}
      />

      <LicenseDetailsPanel
        isOpen={isDetailsPanelOpen}
        onClose={() => setIsDetailsPanelOpen(false)}
        licenseData={selectedLicense}
      />
    </>
  );
}

export default LicenseIntelligence;
